<template>
  <div class="standing-wave-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$λ_n = 2L / n$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
             <label>当前谐波次数</label>
             <span class="val">n = {{ n }}</span>
          </div>
          <div class="hud-item orange">
             <label>波节数量 (Nodes)</label>
             <span class="val">{{ n + 1 }}</span>
          </div>
        </div>

        <svg width="100%" height="220" viewBox="0 0 400 220" preserveAspectRatio="xMidYMid meet">
          <!-- 琴弦背景 -->
          <line x1="50" y1="110" x2="350" y2="110" stroke="#1e293b" stroke-width="4" stroke-linecap="round" />
          
          <!-- 动态驻波 (Standing Wave) -->
          <path :d="wavePath" fill="none" stroke="#fbbf24" stroke-width="3" filter="url(#glow-wave)" />
          <path :d="wavePathMirrored" fill="none" stroke="#fbbf24" stroke-width="1.5" stroke-dasharray="4,2" opacity="0.4" />

          <!-- 波节指示点 (Nodes) -->
          <g v-for="idx in (n + 1)" :key="'node-'+idx">
             <circle :cx="50 + (idx-1) * (300/n)" cy="110" r="4" fill="#64748b" />
          </g>

          <defs>
             <filter id="glow-wave"><feGaussianBlur stdDeviation="3" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
          </defs>
        </svg>

        <div class="physics-caption">
           现象：两列反向传播的相干波叠加，形成了位置固定的波节和振幅最大的波腹。
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex-row items-center justify-between">
              <label class="s-label">选择谐波模式 (Harmonic Modes)：</label>
              <!-- 核心修改：改为横向一行 -->
              <div class="btn-row">
                 <el-button v-for="mode in 4" :key="mode" 
                            :type="n === mode ? 'primary' : 'info'" 
                            size="mini" plain 
                            @click="n = mode">
                   n={{ mode }} {{ mode === 1 ? '(基频)' : '' }}
                 </el-button>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-bell"></i>
           <p><strong>实战物理：</strong>这就是乐器发声的原理。<strong>观察：</strong>随着 $ n $ 增加，驻波的波长线性变短，振奋频率加倍。波节（灰色点）是位移永远为零的位置。只有当外加频率匹配这些固有频率时，系统才会产生强烈的共振。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'StandingWaveViz',
  data() {
    return {
      n: 1,
      t: 0
    }
  },
  computed: {
    wavePath() {
       let points = [];
       const amp = 40;
       // Wave function: y = A * sin(n*pi*x/L) * cos(omega*t)
       const cosT = Math.cos(this.t * 0.1 * this.n); 
       for (let x = 0; x <= 300; x += 5) {
          const y = amp * Math.sin((this.n * Math.PI * x) / 300) * cosT;
          points.push(`${50 + x},${110 + y}`);
       }
       return "M " + points.join(" L ");
    },
    wavePathMirrored() {
       let points = [];
       const amp = 40;
       const cosT = -Math.cos(this.t * 0.1 * this.n); 
       for (let x = 0; x <= 300; x += 5) {
          const y = amp * Math.sin((this.n * Math.PI * x) / 300) * cosT;
          points.push(`${50 + x},${110 + y}`);
       }
       return "M " + points.join(" L ");
    }
  },
  methods: {
    animate() {
       this.t++;
       this.animFrame = requestAnimationFrame(this.animate);
    }
  },
  mounted() { this.animate(); },
  beforeDestroy() { cancelAnimationFrame(this.animFrame); }
}
</script>

<style scoped lang="scss">
.standing-wave-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f9ff; color: #0369a1; border: 1px solid #bae6fd; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 220px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #334155; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #f8fafc; font-weight: 800; } &.cyan { border-left: 3px solid #0ea5e9; } &.orange { border-left: 3px solid #fbbf24; } } }
.physics-caption { position: absolute; bottom: 8px; width: 100%; text-align: center; color: #334155; font-size: 11px; font-weight: bold; }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #ffffff; padding: 12px 14px; border-radius: 12px; border: 1px solid #e2e8f0; }
.btn-row { display: flex; gap: 8px; }
.s-label { font-size: 11px; color: #64748b; font-weight: 800; margin-right: 12px; }
.flex-row { display: flex; flex-direction: row; } .items-center { align-items: center; } .justify-between { justify-content: space-between; }
.discovery-card-compact { background: #f0f9ff; padding: 10px 14px; border-radius: 10px; border: 1px solid #e0f2fe; display: flex; align-items: center; gap: 10px; i { color: #0ea5e9; font-size: 1.2rem; } p { flex: 1; margin: 0; font-size: 11px; color: #0369a1; line-height: 1.5; strong { color: #0c4a6e; } } }
</style>
